import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  PageHeader,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import Content from '../../component/Content';
import fetchApiPolicyDocs from '../../api/apiPolicyDocs';
import { notifyError } from '../../utils/notify';

const { Option } = Select;
const { Text } = Typography;

const methodColor = {
  GET: 'green',
  POST: 'blue',
  PUT: 'gold',
  DELETE: 'red',
  PATCH: 'purple',
  ALL: 'default',
};

const getPolicyColor = (policy = '') => {
  const normalizedPolicy = String(policy).toLowerCase();
  if (normalizedPolicy === 'isauthenticated') return 'violet';
  if (
    normalizedPolicy.includes('clusteradmin') ||
    normalizedPolicy.includes('adminisauthenticated')
  ) {
    return 'red';
  }
  if (
    normalizedPolicy.includes('hubadmin') ||
    normalizedPolicy.includes('groupadmin')
  ) {
    return 'orange';
  }
  if (normalizedPolicy === 'true') return 'green';
  return 'blue';
};

const ApiPolicyDocs = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalRoutes: 0,
    defaultPolicyCount: 0,
    uniqueControllers: 0,
    uniquePolicies: [],
  });
  const [routes, setRoutes] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedMethod, setSelectedMethod] = useState(undefined);
  const [selectedPolicy, setSelectedPolicy] = useState(undefined);

  useEffect(() => {
    const loadRoutePolicies = async () => {
      setLoading(true);
      try {
        const { data } = await fetchApiPolicyDocs();
        const routeRows = Array.isArray(data?.routes) ? data.routes : [];
        setRoutes(
          routeRows.map((route, index) => ({
            ...route,
            key: `${route.httpMethod}-${route.urlPath}-${index}`,
          }))
        );
        setStats({
          totalRoutes: data?.totalRoutes || 0,
          defaultPolicyCount: data?.defaultPolicyCount || 0,
          uniqueControllers: data?.uniqueControllers || 0,
          uniquePolicies: data?.uniquePolicies || [],
        });
      } catch (error) {
        notifyError(error, false, 'Failed to fetch API policy docs.');
      } finally {
        setLoading(false);
      }
    };
    loadRoutePolicies();
  }, []);

  const filteredRoutes = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return routes.filter(route => {
      if (selectedMethod && route.httpMethod !== selectedMethod) return false;
      if (
        selectedPolicy &&
        selectedPolicy !== '__default__' &&
        !route.policies?.some(
          policy => String(policy).toLowerCase() === selectedPolicy
        )
      ) {
        return false;
      }
      if (
        selectedPolicy === '__default__' &&
        !(
          route.isDefault ||
          (route.policies?.length === 1 &&
            String(route.policies[0]).toLowerCase() === 'isauthenticated')
        )
      ) {
        return false;
      }
      if (!query) return true;
      const searchable = [
        route.urlPath,
        route.controller,
        route.action,
        ...(route.policies || []),
      ]
        .join(' ')
        .toLowerCase();
      return searchable.includes(query);
    });
  }, [routes, searchText, selectedMethod, selectedPolicy]);

  const exportCsv = () => {
    const csvRows = [
      ['Method', 'URL Path', 'Controller', 'Action', 'Policies'].join(','),
    ];
    filteredRoutes.forEach(route => {
      csvRows.push(
        [
          route.httpMethod,
          `"${route.urlPath}"`,
          `"${route.controller}"`,
          `"${route.action}"`,
          `"${(route.policies || []).join(', ')}"`,
        ].join(',')
      );
    });
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'api-route-policy-index.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const columns = [
    {
      title: 'Method',
      dataIndex: 'httpMethod',
      key: 'httpMethod',
      width: 110,
      sorter: (a, b) => a.httpMethod.localeCompare(b.httpMethod),
      render: value => (
        <Tag color={methodColor[value] || 'default'}>{value}</Tag>
      ),
    },
    {
      title: 'URL Path',
      dataIndex: 'urlPath',
      key: 'urlPath',
      sorter: (a, b) => a.urlPath.localeCompare(b.urlPath),
      render: value => <code>{value}</code>,
    },
    {
      title: 'Controller',
      dataIndex: 'controller',
      key: 'controller',
      sorter: (a, b) => a.controller.localeCompare(b.controller),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      sorter: (a, b) => a.action.localeCompare(b.action),
    },
    {
      title: 'Policies',
      dataIndex: 'policies',
      key: 'policies',
      render: policies =>
        (policies || []).map(policy => (
          <Tag
            key={policy}
            color={getPolicyColor(policy)}
            style={{ marginBottom: 4 }}
          >
            {policy}
          </Tag>
        )),
    },
  ];

  return (
    <>
      <PageHeader title="API Policy Docs" />
      <Content>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title="Total Routes" value={stats.totalRoutes} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Default Policy (isAuthenticated)"
                value={stats.defaultPolicyCount}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title="Controllers" value={stats.uniqueControllers} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Unique Policies"
                value={stats.uniquePolicies.length}
              />
            </Card>
          </Col>
        </Row>
        <Card>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <Input
              style={{ width: 320 }}
              placeholder="Search by path, controller, action, or policy"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              allowClear
            />
            <Select
              style={{ width: 160 }}
              value={selectedMethod}
              onChange={value => setSelectedMethod(value)}
              allowClear
              placeholder="All Methods"
            >
              {['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'ALL'].map(method => (
                <Option key={method} value={method}>
                  {method}
                </Option>
              ))}
            </Select>
            <Select
              style={{ width: 240 }}
              value={selectedPolicy}
              onChange={value => setSelectedPolicy(value)}
              allowClear
              placeholder="All Policies"
            >
              <Option value="__default__">
                Default (isAuthenticated only)
              </Option>
              {(stats.uniquePolicies || []).map(policy => (
                <Option key={policy} value={policy}>
                  {policy}
                </Option>
              ))}
            </Select>
            <Text type="secondary">
              {filteredRoutes.length} of {routes.length} routes
            </Text>
            <Button onClick={exportCsv}>Export CSV</Button>
          </div>
          <Table
            loading={loading}
            dataSource={filteredRoutes}
            columns={columns}
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: true }}
          />
        </Card>
      </Content>
    </>
  );
};

export default ApiPolicyDocs;
